import { useCallback, useEffect, useRef } from 'react';
import { FUNNEL_URL } from '../../config';

const FUNNEL_ORIGIN = new URL(FUNNEL_URL).origin;
const POLL_MS = 500;

// Opens Google/Facebook as a separate top-level popup -- the only part of
// this flow that isn't a plain in-page fetch, since neither provider will
// render its consent screen in an iframe. The funnel has a custom
// pages.signIn configured, so a plain GET to /api/auth/signin/{provider}
// just redirects to that page instead of starting OAuth -- the real
// protocol (what next-auth/react's own signIn() does) is a POST with a
// CSRF token, which returns the actual provider authorization URL as JSON.
// callbackUrl = the funnel's popup-complete page, which closes the popup
// for us once it's done.
export function useOAuthPopup() {
  const popupRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      try {
        popupRef.current.close();
      } catch {
        // Cross-origin popups can't always be closed from here; harmless.
      }
    }
    popupRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  // onComplete always fires (closed, success, or timeout) -- the caller
  // must independently re-verify the session, never trust this alone.
  const openOAuthPopup = useCallback(
    (provider, onComplete) => {
      cleanup();

      const width = 500;
      const height = 650;
      const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

      // Opened blank, synchronously, so the browser still treats this as a
      // direct response to the click (required for Safari/mobile) -- the
      // CSRF + POST dance below is async and would otherwise happen too
      // late for window.open to count as user-triggered.
      let popup = null;
      try {
        popup = window.open(
          '',
          'romaya-oauth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );
      } catch {
        popup = null;
      }

      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        return { blocked: true };
      }

      popupRef.current = popup;
      try {
        popup.focus();
      } catch {
        // Focus can be denied by the browser; not fatal either way.
      }

      const finish = () => {
        cleanup();
        onComplete();
      };

      const handleMessage = (event) => {
        if (event.origin !== FUNNEL_ORIGIN) return;
        if (!event.data || event.data.type !== 'romaya-oauth-complete') return;
        window.removeEventListener('message', handleMessage);
        finish();
      };
      window.addEventListener('message', handleMessage);

      timerRef.current = setInterval(() => {
        if (popup.closed) {
          window.removeEventListener('message', handleMessage);
          finish();
        }
      }, POLL_MS);

      (async () => {
        try {
          const csrfRes = await fetch(`${FUNNEL_URL}/api/auth/csrf`, { credentials: 'include' });
          const { csrfToken } = await csrfRes.json();
          const popupCompleteUrl = `${FUNNEL_URL}/auth/popup-complete`;
          const body = new URLSearchParams({ csrfToken, callbackUrl: popupCompleteUrl, json: 'true' });

          const res = await fetch(`${FUNNEL_URL}/api/auth/signin/${provider}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
          });
          const data = await res.json();

          if (popup.closed) return;
          if (data && data.url) {
            popup.location.href = data.url;
          } else {
            finish();
          }
        } catch {
          if (!popup.closed) finish();
        }
      })();

      return { blocked: false };
    },
    [cleanup],
  );

  return { openOAuthPopup, cancelOAuthPopup: cleanup };
}
